import {getCurrentInstance, onUnmounted} from 'vue';
import dayjs from "dayjs";

const socketPool = new Map();

class Socket {
    url;
    ws = null;
    opts;
    reconnectAttempts = 0;
    listeners = {};
    heartbeatInterval = null;
    reconnectTimer = null;
    connectTimer = null;
    manuallyClosed = false;
    lastReceivedAt = 0;
    visibilityHandler = null;

    constructor(url, opts = {}) {
        this.url = url;
        this.opts = {
            // 心跳检测
            heartbeatInterval: 30000,
            // 心跳超时：超过该时间未收到任何消息(含心跳响应)，认为连接已失活，将主动 close 触发重连
            heartbeatTimeout: 65000,
            // 连接建立超时(OPEN 前超过该时间)，认为连接卡住，将主动 close 触发重连
            connectTimeout: 15000,
            // 重新连接时间间隔
            reconnectInterval: 5000,
            reconnectMaxInterval: 60000,
            reconnectBackoffFactor: 1.8,
            reconnectJitterRatio: 0.2,
            // 重连的次数
            maxReconnectAttempts: Number.POSITIVE_INFINITY,
            heartbeatMessage: 'ping',
            // 服务端下发 ping 时，客户端的响应内容（如不需要可传 null）
            pingReplyMessage: 'pong',
            ...opts
        };

        this.bindVisibilityChange();

        this.connect();
    }

    bindVisibilityChange() {
        if (typeof document === 'undefined' || this.visibilityHandler) {
            return;
        }

        this.visibilityHandler = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            if (this.manuallyClosed) {
                return;
            }

            const rs = this.ws?.readyState;
            if (rs === WebSocket.CLOSED || rs === WebSocket.CLOSING || rs === undefined) {
                this.connect(true);
                return;
            }

            if (rs === WebSocket.OPEN) {
                const msg = this.opts.heartbeatMessage;
                if (msg !== null && msg !== undefined) {
                    try {
                        this.ws.send(msg);
                    } catch (e) {
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    unbindVisibilityChange() {
        if (typeof document === 'undefined' || !this.visibilityHandler) {
            return;
        }
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.visibilityHandler = null;
    }

    init() {
        return this.connect();
    }

    connect(force = false) {
        const readyState = this.ws?.readyState;
        const isAlive = readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING;

        if (!force && isAlive) {
            return this.ws;
        }

        // 避免 force 重连时，CONNECTING 状态下重复创建 socket（容易造成服务端主动断开/抖动）
        if (force && readyState === WebSocket.CONNECTING) {
            return this.ws;
        }

        this.clearReconnectTimer();
        this.stopHeartbeat();
        this.clearConnectTimer();
        this.manuallyClosed = false;

        const nextSocket = new WebSocket(this.url);
        this.ws = nextSocket;
        nextSocket.onopen = (event) => this.onOpen(event, nextSocket);
        nextSocket.onmessage = (event) => this.onMessage(event, nextSocket);
        nextSocket.onerror = (event) => this.onError(event, nextSocket);
        nextSocket.onclose = (event) => this.onClose(event, nextSocket);

        this.startConnectTimeout(nextSocket);

        return nextSocket;
    }

    onOpen(event, currentSocket) {
        if (currentSocket !== this.ws) {
            return;
        }

        if (event) {
            console.log(`%c websocket 已建立连接 ${dayjs(new Date()).format("HH:mm:ss")}`, 'color: green');
            this.lastReceivedAt = Date.now();
            this.reconnectAttempts = 0;
            this.clearReconnectTimer();
            this.clearConnectTimer();
            this.startHeartbeat();
            this.emit('open', event);
        }

    }

    onMessage(event, currentSocket) {
        if (currentSocket !== this.ws) {
            return;
        }

        this.lastReceivedAt = Date.now();
        const data = event?.data;

        if (data === 'pong') {
            return;
        }

        // 某些服务端会用 websocket 文本消息下发 ping，需要客户端回复 pong 否则会断开
        if (data === 'ping') {
            const reply = this.opts?.pingReplyMessage;
            if (reply !== null && reply !== undefined) {
                try {
                    if (this.ws?.readyState === WebSocket.OPEN) {
                        this.ws.send(reply);
                    }
                } catch (e) {
                }
            }
            return;
        }

        if (data instanceof Blob) {
            data.text().then((text) => {
                if (currentSocket !== this.ws) return;
                this.emit('message', text);
            }).catch(() => {
                if (currentSocket !== this.ws) return;
                this.emit('message', data);
            });
            return;
        }

        if (data instanceof ArrayBuffer) {
            try {
                const text = new TextDecoder('utf-8').decode(new Uint8Array(data));
                this.emit('message', text);
            } catch (e) {
                this.emit('message', data);
            }
            return;
        }

        this.emit('message', data);
    }

    onError(event, currentSocket) {
        if (currentSocket !== this.ws) {
            return;
        }

        console.error('WebSocket error:', event);
        this.emit('error', event);

        if (!this.manuallyClosed) {
            const rs = this.ws?.readyState;
            if (rs === WebSocket.CLOSED || rs === WebSocket.CLOSING) {
                this.scheduleReconnect();
            }
        }
    }

    onClose(event, currentSocket) {
        if (currentSocket !== this.ws) {
            return;
        }

        console.log(
            `%c websocket 已断开连接 ${dayjs(new Date()).format("HH:mm:ss")}`,
            'color: red',
            {
                code: event?.code,
                reason: event?.reason,
                wasClean: event?.wasClean
            }
        );
        this.stopHeartbeat();
        this.clearConnectTimer();
        this.emit('close', event);

        if (!this.manuallyClosed) {
            this.scheduleReconnect();
        }
    }

    startHeartbeat() {
        if (!this.opts.heartbeatInterval || this.heartbeatInterval) return;

        this.heartbeatInterval = window.setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                console.log(`%c ❤ 检测 ${dayjs(new Date()).format("HH:mm:ss")}`, 'color: yellow');

                if (this.opts.heartbeatTimeout && this.lastReceivedAt && document.visibilityState === 'visible') {
                    const now = Date.now();
                    if (now - this.lastReceivedAt > this.opts.heartbeatTimeout) {
                        console.warn('WebSocket heartbeat timeout, forcing reconnect');
                        try {
                            const rs = this.ws?.readyState;
                            if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) {
                                this.ws.close();
                            }
                        } catch (e) {
                        }
                        return;
                    }
                }

                if (this.opts.heartbeatMessage !== null && this.opts.heartbeatMessage !== undefined) {
                    try {
                        this.ws.send(this.opts.heartbeatMessage);
                    } catch (e) {
                    }
                }
            }
        }, this.opts.heartbeatInterval);
    }

    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    startConnectTimeout(currentSocket) {
        if (!this.opts.connectTimeout) {
            return;
        }

        this.connectTimer = window.setTimeout(() => {
            if (currentSocket !== this.ws) {
                return;
            }

            const rs = this.ws?.readyState;
            if (rs === WebSocket.CONNECTING) {
                try {
                    this.ws.close();
                } catch (e) {
                }
            }
        }, this.opts.connectTimeout);
    }

    clearConnectTimer() {
        if (this.connectTimer) {
            clearTimeout(this.connectTimer);
            this.connectTimer = null;
        }
    }

    computeReconnectDelay() {
        const base = Number(this.opts.reconnectInterval) || 0;
        if (base <= 0) return 0;

        const factor = Number(this.opts.reconnectBackoffFactor) || 1;
        const max = Number(this.opts.reconnectMaxInterval) || base;
        const attempt = Math.max(0, Number(this.reconnectAttempts) || 0);

        let delay = base * Math.pow(factor, Math.max(0, attempt - 1));
        if (!Number.isFinite(delay)) {
            delay = max;
        }
        delay = Math.min(delay, max);

        const jitterRatio = Math.max(0, Number(this.opts.reconnectJitterRatio) || 0);
        if (jitterRatio > 0) {
            const jitter = delay * jitterRatio * (Math.random() * 2 - 1);
            delay = Math.max(0, Math.floor(delay + jitter));
        }

        return delay;
    }

    scheduleReconnect() {
        if (this.reconnectTimer || this.manuallyClosed) {
            return;
        }

        if (this.reconnectAttempts >= this.opts.maxReconnectAttempts) {
            return;
        }

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            const onOnline = () => {
                window.removeEventListener('online', onOnline);
                if (!this.manuallyClosed) {
                    this.connect(true);
                }
            };
            window.addEventListener('online', onOnline);
            return;
        }

        const delay = this.computeReconnectDelay();

        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = null;
            if (this.manuallyClosed) {
                return;
            }

            this.reconnectAttempts += 1;
            this.connect(true);
        }, delay);
    }

    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    send(data) {
        const rs = this.ws?.readyState;
        if (rs !== WebSocket.OPEN) {
            console.error('WebSocket is not open. Cannot send:', data);
            return false;
        }

        try {
            this.ws.send(data);
            return true;
        } catch (e) {
            console.error('WebSocket send failed:', e);
            return false;
        }
    }

    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        // Avoid duplicate registration (can happen if callers re-bind on reconnect)
        if (!this.listeners[event].includes(callback)) {
            this.listeners[event].push(callback);
        }
    }

    off(event, callback) {
        if (!this.listeners[event]) {
            return;
        }

        if (!callback) {
            delete this.listeners[event];
            return;
        }

        this.listeners[event] = this.listeners[event].filter(listener => listener !== callback);
        if (!this.listeners[event].length) {
            delete this.listeners[event];
        }
    }

    emit(event, data) {
        const cbs = this.listeners[event];
        if (!cbs || !cbs.length) {
            return;
        }

        // Never let a single handler exception break the websocket message pipeline.
        // (Uncaught errors inside onmessage can cause subsequent processing to behave unpredictably.)
        cbs.forEach((callback) => {
            try {
                callback(data);
            } catch (e) {
                console.error('[WebSocket] listener error:', {event, error: e});
            }
        });
    }

    close(options = {}) {
        const {manual = true} = options;
        this.manuallyClosed = manual;
        this.clearReconnectTimer();
        this.stopHeartbeat();
        this.clearConnectTimer();
        if (manual) {
            this.unbindVisibilityChange();
        }

        if (!this.ws) {
            return;
        }

        const readyState = this.ws.readyState;
        if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
            this.ws.close();
        }

        if (manual) {
            const pooled = socketPool.get(this.url);
            if (pooled?.socket === this) {
                socketPool.delete(this.url);
            }
        }
    }
}

export function useSocket(url, opts) {
    const existed = socketPool.get(url);
    if (existed?.socket) {
        existed.refs += 1;
        const socket = existed.socket;

        if (getCurrentInstance()) {
            onUnmounted(() => {
                existed.refs -= 1;
                if (existed.refs <= 0) {
                    socket.close({manual: true});
                    socketPool.delete(url);
                }
            });
        }

        return {
            socket,
            connect: socket.connect.bind(socket),
            close: socket.close.bind(socket),
            send: socket.send.bind(socket),
            on: socket.on.bind(socket),
            off: socket.off.bind(socket)
        };
    }

    const socket = new Socket(url, opts);
    socketPool.set(url, {socket, refs: 1});

    if (getCurrentInstance()) {
        onUnmounted(() => {
            const pooled = socketPool.get(url);
            if (pooled?.socket === socket) {
                pooled.refs -= 1;
                if (pooled.refs <= 0) {
                    socket.close({manual: true});
                    socketPool.delete(url);
                }
            }
        });
    }

    return {
        socket,
        connect: socket.connect.bind(socket),
        close: socket.close.bind(socket),
        send: socket.send.bind(socket),
        on: socket.on.bind(socket),
        off: socket.off.bind(socket)
    };
}
